import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IDashboard, Widget } from "@/types/dashboard.types";
import { EmptyState } from "@/components/ui/empty-state";

interface DashboardsListProps {
  dashboards: IDashboard[];
}

// Widgets referenced as group children are represented by their group entry
export function getListedWidgets(dashboard: IDashboard): Widget[] {
  const widgets = dashboard.widgets ?? [];
  const childIds = new Set<string>(
    widgets.flatMap((widget) => (widget as any).config?.group?.childrenIds ?? [])
  );
  const orderIndex = new Map((dashboard.widgetOrder ?? []).map((id, index) => [id, index]));

  return widgets
    .filter((widget) => !childIds.has(widget.id))
    .sort((a, b) => {
      const aIndex = orderIndex.get(a.id);
      const bIndex = orderIndex.get(b.id);
      if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
      if (aIndex !== undefined) return -1;
      if (bIndex !== undefined) return 1;
      return a.title.localeCompare(b.title);
    });
}

export function DashboardsList({ dashboards }: DashboardsListProps) {
  const { t } = useTranslation();

  if (!dashboards || dashboards.length === 0) {
    return (
      <EmptyState
        title={t("dashboards.noDashboardsAvailable")}
        description={t("dashboards.noAccessToDashboards")}
        icon={TrendingUp}
      />
    );
  }

  const sortedDashboards = [...dashboards].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="bg-white space-y-7">
      {sortedDashboards.map((dashboard) => {
        const widgets = getListedWidgets(dashboard);
        return (
          <div key={dashboard.id} className="px-4 py-3 rounded-lg border-l-[2.4px] border-darkBlue">
            <h2 className="text-xl font-medium mb-3 text-black hover:text-[#001544]">
              <Link to={`/dashboards/${dashboard.id}`}>{dashboard.name}</Link>
            </h2>
            {widgets.length > 0 ? (
              <div className="list-disc list-inside space-y-3">
                {widgets.map((widget) => (
                  <div
                    key={widget.id}
                    className="text-black hover:text-[#001544] text-sm hover:underline"
                  >
                    <Link to={`/dashboards/${dashboard.id}`}>{widget.title}</Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("dashboards.noWidgetsConfigured")}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
